// Sidebar chat list: Pinned, recency groups (Today / Yesterday / Last 7
// days / Last 30 days / Older) and folders, with a title/message filter,
// multi-select (checkbox, Cmd/Ctrl-click, Shift-click ranges) and HTML5
// drag & drop into folders and back out to "no folder".
//
// Keyboard users move chats through the row menu ("Move to folder") or
// the bulk bar in Sidebar; drag & drop is a mouse shortcut on top.

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { ChatSession, ChatFolder } from '../../types';
import { groupByRecency, matchesFilter, rangeBetween, RECENCY_LABEL } from '../../lib/chatGroups';

export function relativeDate(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

/** Drop target key for "remove from folder". */
const UNFILED = 'unfiled';
const DRAG_MIME = 'application/x-malpedia-chat';

interface FolderTreeProps {
  sessions: ChatSession[];
  /** Chats with a turn still running server-side. */
  runningIds?: ReadonlySet<string>;
  folders: ChatFolder[];
  activeSessionId: string | null;
  folderCollapseState: Record<string, boolean>;
  renamingId: string | null;
  renamingType: 'session' | 'folder' | null;
  renameValue: string;
  /** Free-text filter; empty shows everything. */
  filter: string;
  /** Extra texts (message bodies already in memory) the filter may match. */
  getSearchTexts?: (session: ChatSession) => Iterable<string>;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onToggleFolderCollapse: (folderId: string) => void;
  onSwitchSession: (id: string) => void;
  onStartRenameSession: (id: string, title: string) => void;
  onStartRenameFolder: (id: string, name: string) => void;
  onRenameValueChange: (value: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onChatMenuOpen: (sessionId: string, rect: DOMRect) => void;
  onFolderMenuOpen: (folderId: string, rect: DOMRect) => void;
  onChatDownload: (sessionId: string) => void;
  onCreateFolder: () => void;
  onMoveSessionsToFolder: (sessionIds: string[], folderId: string | undefined) => void;
}

export default function FolderTree({
  sessions,
  runningIds,
  folders,
  activeSessionId,
  folderCollapseState,
  renamingId,
  renamingType,
  renameValue,
  filter,
  getSearchTexts,
  selectedIds,
  onSelectionChange,
  onToggleFolderCollapse,
  onSwitchSession,
  onStartRenameSession,
  onStartRenameFolder,
  onRenameValueChange,
  onCommitRename,
  onCancelRename,
  onChatMenuOpen,
  onFolderMenuOpen,
  onChatDownload,
  onCreateFolder,
  onMoveSessionsToFolder,
}: FolderTreeProps) {
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Drag-and-drop state. `draggedIds` is the whole selection when a
  // selected chat is dragged, otherwise just the one chat.
  const [draggedIds, setDraggedIds] = useState<string[] | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);

  // Anchor for Shift-click range selection.
  const anchorRef = useRef<string | null>(null);

  // Focus rename input when it appears
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const filtering = filter.trim().length > 0;

  // ── Grouping ──────────────────────────────────────────────────────────
  const groups = useMemo(() => {
    const visible = filtering
      ? sessions.filter((s) =>
          matchesFilter(
            s.title || (s.temporary ? 'Temporary chat' : 'New chat'),
            filter,
            getSearchTexts?.(s),
          ),
        )
      : sessions;

    const folderIds = new Set(folders.map((f) => f.id));
    const byUpdate = (a: ChatSession, b: ChatSession) => b.updatedAt - a.updatedAt;

    const pinned = visible.filter((s) => s.pinned).sort(byUpdate);
    const byFolder = new Map<string, ChatSession[]>();
    const loose: ChatSession[] = [];
    for (const s of visible) {
      if (s.folderId && folderIds.has(s.folderId)) {
        // Pinned chats stay listed in their folder too, so the folder's
        // contents don't change when a chat is pinned.
        const arr = byFolder.get(s.folderId) ?? [];
        arr.push(s);
        byFolder.set(s.folderId, arr);
      } else if (!s.pinned) {
        loose.push(s);
      }
    }
    for (const arr of byFolder.values()) arr.sort(byUpdate);

    const recency = groupByRecency(loose);
    const sortedFolders = [...folders].sort((a, b) => a.createdAt - b.createdAt);
    return { pinned, byFolder, recency, sortedFolders };
  }, [sessions, folders, filter, filtering, getSearchTexts]);

  // Recent buckets render above the folders (as in the approved mockup);
  // older ones below.
  const earlyRecency = groups.recency.filter((g) => g.bucket === 'today' || g.bucket === 'yesterday');
  const lateRecency = groups.recency.filter((g) => g.bucket !== 'today' && g.bucket !== 'yesterday');

  const isFolderOpen = useCallback(
    (folderId: string) => filtering || !(folderCollapseState[folderId] ?? false),
    [filtering, folderCollapseState],
  );

  const visibleFolders = filtering
    ? groups.sortedFolders.filter((f) => (groups.byFolder.get(f.id)?.length ?? 0) > 0)
    : groups.sortedFolders;

  // Top-to-bottom order of the rows on screen, for range selection.
  const visibleOrder = (() => {
    const order: string[] = [];
    for (const s of groups.pinned) order.push(s.id);
    for (const g of earlyRecency) for (const s of g.sessions) order.push(s.id);
    for (const f of visibleFolders) {
      if (isFolderOpen(f.id)) for (const s of groups.byFolder.get(f.id) ?? []) order.push(s.id);
    }
    for (const g of lateRecency) for (const s of g.sessions) order.push(s.id);
    return order;
  })();

  // ── Selection ─────────────────────────────────────────────────────────
  const toggleSelected = useCallback((id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    anchorRef.current = id;
    onSelectionChange(next);
  }, [selectedIds, onSelectionChange]);

  const selectRange = useCallback((id: string) => {
    const anchor = anchorRef.current;
    if (!anchor) {
      toggleSelected(id);
      return;
    }
    const next = new Set(selectedIds);
    for (const x of rangeBetween(visibleOrder, anchor, id)) next.add(x);
    onSelectionChange(next);
  }, [selectedIds, visibleOrder, onSelectionChange, toggleSelected]);

  const selecting = selectedIds.size > 0;

  // ── Drag & drop helpers ───────────────────────────────────────────────
  const draggedFromFolder = !!draggedIds?.some(
    (id) => !!sessions.find((s) => s.id === id)?.folderId,
  );

  const dropProps = (target: string, folderId: string | undefined) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!draggedIds) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverTarget !== target) setDragOverTarget(target);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Ignore leave events fired when crossing into a child element.
      if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
      setDragOverTarget((prev) => (prev === target ? null : prev));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (draggedIds && draggedIds.length > 0) {
        const toMove = draggedIds.filter(
          (id) => (sessions.find((s) => s.id === id)?.folderId ?? undefined) !== folderId,
        );
        if (toMove.length > 0) onMoveSessionsToFolder(toMove, folderId);
        // Auto-expand the folder that received the chats.
        if (folderId && folderCollapseState[folderId]) onToggleFolderCollapse(folderId);
      }
      setDraggedIds(null);
      setDragOverTarget(null);
    },
  });

  const renderRenameInput = () => (
    <input
      ref={renameInputRef}
      className="malpedia-sidebar-rename-input"
      value={renameValue}
      onChange={(e) => onRenameValueChange(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') onCommitRename();
        if (e.key === 'Escape') onCancelRename();
      }}
      onBlur={onCommitRename}
      onClick={(e) => e.stopPropagation()}
    />
  );

  const renderChatItem = (session: ChatSession, nested: boolean, group: string) => {
    const title = session.title || (session.temporary ? 'Temporary chat' : 'New chat');
    const selected = selectedIds.has(session.id);
    const dragging = !!draggedIds?.includes(session.id);
    return (
      <div
        key={`${group}:${session.id}`}
        className={
          'malpedia-sidebar-session-item malpedia-chatlist-item' +
          (session.id === activeSessionId ? ' active' : '') +
          (nested ? ' nested' : '') +
          (session.temporary ? ' temporary' : '') +
          (dragging ? ' dragging' : '') +
          (selected ? ' selected' : '')
        }
        tabIndex={0}
        aria-current={session.id === activeSessionId ? 'page' : undefined}
        onClick={(e) => {
          if (e.shiftKey) {
            e.preventDefault();
            selectRange(session.id);
            return;
          }
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            toggleSelected(session.id);
            return;
          }
          onSwitchSession(session.id);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter') {
            e.preventDefault();
            onSwitchSession(session.id);
          } else if (e.key === ' ') {
            e.preventDefault();
            if (e.shiftKey) selectRange(session.id);
            else toggleSelected(session.id);
          } else if (e.key === 'F2') {
            e.preventDefault();
            onStartRenameSession(session.id, session.title);
          } else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            onChatMenuOpen(session.id, e.currentTarget.getBoundingClientRect());
          }
        }}
        onDoubleClick={() => onStartRenameSession(session.id, session.title)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
          onChatMenuOpen(session.id, rect);
        }}
        draggable={renamingId !== session.id}
        onDragStart={(e) => {
          const ids = selected && selectedIds.size > 1 ? Array.from(selectedIds) : [session.id];
          setDraggedIds(ids);
          e.dataTransfer.setData(DRAG_MIME, ids.join(','));
          e.dataTransfer.setData('text/plain', title);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDraggedIds(null);
          setDragOverTarget(null);
        }}
      >
        {renamingId === session.id && renamingType === 'session' ? (
          renderRenameInput()
        ) : (
          <>
            <input
              type="checkbox"
              className="malpedia-chatlist-check"
              checked={selected}
              aria-label={`Select “${title}”`}
              onChange={() => { /* handled in onClick to read shiftKey */ }}
              onClick={(e) => {
                e.stopPropagation();
                if (e.shiftKey) selectRange(session.id);
                else toggleSelected(session.id);
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <span className="malpedia-sidebar-session-title">{title}</span>
            {runningIds?.has(session.id) && (
              <span
                className="malpedia-chatlist-running"
                role="status"
                aria-label="Reply in progress"
                title="Reply in progress"
              />
            )}
            {session.pinned && (
              <svg
                className="malpedia-chatlist-pin"
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="currentColor"
                role="img"
                aria-label="Pinned"
              >
                <path d="M10.5 1.5l4 4-2 1-2.5 2.5.5 3.5-1.5 1.5-3-3-4 4-1-1 4-4-3-3L3.5 6l3.5.5L9.5 4z" />
              </svg>
            )}
            {session.serverIsSensitive && (
              <span
                className="malpedia-sidebar-session-sensitive"
                title="Marked sensitive — excluded from any future cross-user features"
                aria-label="Sensitive"
              >
                SENSITIVE
              </span>
            )}
            <span className="malpedia-sidebar-session-time">
              {relativeDate(session.updatedAt)}
            </span>
            <button
              className="malpedia-sidebar-session-action"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onChatDownload(session.id);
              }}
              aria-label="Download chat"
              title="Download as markdown"
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 2v8M4 7l3 3 3-3" />
                <path d="M2.5 11.5h9" />
              </svg>
            </button>
            <button
              className="malpedia-sidebar-session-action"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                onChatMenuOpen(session.id, rect);
              }}
              aria-label={`Options for “${title}”`}
              title="More options"
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor">
                <circle cx="7" cy="3" r="1.2" />
                <circle cx="7" cy="7" r="1.2" />
                <circle cx="7" cy="11" r="1.2" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  const renderRecencyGroup = (g: { bucket: string; sessions: ChatSession[] }) => {
    const target = `${UNFILED}:${g.bucket}`;
    return (
      <section
        key={g.bucket}
        className={`malpedia-chatlist-group${dragOverTarget === target ? ' drop-target' : ''}`}
        aria-label={RECENCY_LABEL[g.bucket as keyof typeof RECENCY_LABEL]}
        {...dropProps(target, undefined)}
      >
        <h4 className="malpedia-chatlist-heading">
          {RECENCY_LABEL[g.bucket as keyof typeof RECENCY_LABEL]}
        </h4>
        {g.sessions.map((s) => renderChatItem(s, false, g.bucket))}
      </section>
    );
  };

  const nothingMatches =
    filtering &&
    groups.pinned.length === 0 &&
    groups.recency.length === 0 &&
    visibleFolders.length === 0;

  return (
    <div
      className={`malpedia-folder-tree malpedia-chatlist${selecting ? ' selecting' : ''}${draggedIds ? ' is-dragging' : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && selecting) {
          e.stopPropagation();
          onSelectionChange(new Set());
        }
      }}
    >
      {/* "Remove from folder" target — only while dragging a foldered chat. */}
      {draggedIds && draggedFromFolder && (
        <div
          className={`malpedia-chatlist-unfile-zone${dragOverTarget === UNFILED ? ' drop-target' : ''}`}
          {...dropProps(UNFILED, undefined)}
        >
          Drop here to remove from folder
        </div>
      )}

      {groups.pinned.length > 0 && (
        <section className="malpedia-chatlist-group" aria-label="Pinned">
          <h4 className="malpedia-chatlist-heading">Pinned</h4>
          {groups.pinned.map((s) => renderChatItem(s, false, 'pinned'))}
        </section>
      )}

      {earlyRecency.map(renderRecencyGroup)}

      {(visibleFolders.length > 0 || !filtering) && (
        <section className="malpedia-chatlist-group" aria-label="Folders">
          <h4 className="malpedia-chatlist-heading">
            Folders
            {!filtering && (
              <button
                className="malpedia-chatlist-heading-action"
                onClick={onCreateFolder}
                aria-label="New folder"
                title="New folder"
              >
                +
              </button>
            )}
          </h4>
          {visibleFolders.map((folder) => {
            const open = isFolderOpen(folder.id);
            const folderSessions = groups.byFolder.get(folder.id) ?? [];
            const count = filtering
              ? folderSessions.length
              : sessions.filter((s) => s.folderId === folder.id).length;
            return (
              <div key={folder.id} className="malpedia-sidebar-folder-group">
                <div
                  className={`malpedia-sidebar-folder-row${dragOverTarget === folder.id ? ' drag-over' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  onClick={() => { if (!filtering) onToggleFolderCollapse(folder.id); }}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if ((e.key === 'Enter' || e.key === ' ') && !filtering) {
                      e.preventDefault();
                      onToggleFolderCollapse(folder.id);
                    } else if (e.key === 'F2') {
                      e.preventDefault();
                      onStartRenameFolder(folder.id, folder.name);
                    } else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                      e.preventDefault();
                      onFolderMenuOpen(folder.id, e.currentTarget.getBoundingClientRect());
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onStartRenameFolder(folder.id, folder.name);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
                    onFolderMenuOpen(folder.id, rect);
                  }}
                  {...dropProps(folder.id, folder.id)}
                >
                  <span className={`malpedia-sidebar-folder-chevron${open ? ' expanded' : ''}`} aria-hidden="true">
                    ▶
                  </span>
                  <svg className="malpedia-chatlist-folder-icon" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
                    <path d="M1.5 4.5a1 1 0 0 1 1-1h3.5l1.5 1.5h6a1 1 0 0 1 1 1v6.5a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1z" />
                  </svg>
                  {renamingId === folder.id && renamingType === 'folder' ? (
                    renderRenameInput()
                  ) : (
                    <span className="malpedia-sidebar-folder-name">{folder.name}</span>
                  )}
                  <span className="malpedia-sidebar-folder-count">{count}</span>
                  <button
                    className="malpedia-sidebar-folder-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      onFolderMenuOpen(folder.id, rect);
                    }}
                    aria-label={`Options for folder “${folder.name}”`}
                    title="Folder options"
                  >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor">
                      <circle cx="7" cy="3" r="1.2" />
                      <circle cx="7" cy="7" r="1.2" />
                      <circle cx="7" cy="11" r="1.2" />
                    </svg>
                  </button>
                </div>
                {open && (
                  <div
                    className="malpedia-sidebar-folder-children"
                    {...dropProps(folder.id, folder.id)}
                  >
                    {folderSessions.length === 0 ? (
                      <div className="malpedia-sidebar-folder-empty">No chats — drag one here</div>
                    ) : (
                      folderSessions.map((s) => renderChatItem(s, true, `folder-${folder.id}`))
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {!filtering && folders.length === 0 && (
            <button className="malpedia-sidebar-new-folder-btn" onClick={onCreateFolder}>
              + New folder
            </button>
          )}
        </section>
      )}

      {lateRecency.map(renderRecencyGroup)}

      {nothingMatches && (
        <div className="malpedia-chatlist-empty">No chats match “{filter.trim()}”</div>
      )}
    </div>
  );
}
